import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Chip, Stack, Divider,
  ToggleButton, ToggleButtonGroup, Tooltip, Dialog, DialogContent, IconButton,
  Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem,
} from '@mui/material';
import { CloudUpload, InsertDriveFile, Refresh, Close, ExpandMore, HelpOutline } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import * as XLSX from 'xlsx';
import RegressionDiagram from './RegressionDiagram';

// Shared "how to read this" explainer, used on the page and in the zoom dialog.
const ChartGuide: React.FC = () => (
  <Box sx={{ '& strong': { fontWeight: 700 } }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>The chart</Typography>
    <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}><Box sx={{ color: '#1976d2' }}>●</Box>
        <span><strong>Actual</strong> (blue) — the meter factor actually measured at each proving.</span></Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}><Box sx={{ color: '#9c27b0' }}>┄</Box>
        <span><strong>Predicted</strong> (purple dashed) — what the regression expected for that proving from its operating conditions (flow, temp, density, pressure).</span></Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}><Box sx={{ color: '#9c27b0', opacity: 0.5 }}>▒</Box>
        <span><strong>95% PI</strong> (light purple band) — the range each prediction is expected to fall within 95% of the time.</span></Box>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}><Box sx={{ color: '#2e7d32' }}>┄</Box>
        <span><strong>Expected next</strong> (green dashed line) — a forecast of where the <em>next, not-yet-taken</em> proving should land, at typical recent conditions. None of the plotted points are this — it's the single future value.</span></Box>
    </Typography>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>The modes (top-right toggle)</Typography>
    <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
      <div><strong>Predict Recent</strong> — train on history, predict only the most recent N provings (set with the “Recent” control). Use to <em>monitor</em> whether the latest provings are behaving.</div>
      <div><strong>Ensemble</strong> — blends several models, each weighted by its honest out-of-sample accuracy. The most accurate view, and where future-proving projections are drawn.</div>
    </Typography>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>The chips</Typography>
    <Typography variant="body2" color="text.secondary" component="div">
      <div><strong>Quality (R²)</strong> — how much of the meter-factor variation the predictors explain. GOOD &gt;70%, FAIR ≥ your min R², POOR below.</div>
      <div><strong>Trust</strong> — out-of-sample R² (walk-forward): how reliably the model predicts provings it hasn't seen. This is what makes the forecast trustworthy or not.</div>
      <div><strong>Level</strong> — how many predictors the model used (0–4).</div>
      <div><strong>Blend</strong> (Ensemble only) — each model's share, weighted by out-of-sample accuracy; 0% means that model didn't help.</div>
    </Typography>
  </Box>
);

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

// Tolerant header → field mapping for the Data Extraction layout.
const FIELD_ALIASES: Record<string, string[]> = {
  date: ['date', 'test date'],
  meter_factor: ['meter factor', 'meterfactor'],
  flow_rate: ['flow rate', 'avg flow rate', 'flowrate'],
  meter_temperature: ['meter temp', 'avg meter temp', 'temp', 'temperature'],
  density: ['density', 'avg meter dens', 'meter dens', 'dens'],
  meter_pressure: ['meter press', 'avg pres', 'pressure', 'press'],
  meter_id: ['meter id', 'meterid'],
  commodity: ['commodity', 'product'],
  direction: ['in/out', 'direction'],
};

const norm = (s: any) => String(s ?? '').trim().toLowerCase();

// Find the sheet holding the proving data: prefer "Data extraction", else a single
// sheet, else the first sheet whose headers include Meter Factor + Meter ID.
function pickDataSheet(wb: XLSX.WorkBook): string | null {
  const byName = wb.SheetNames.find((n) => norm(n).includes('data extraction'));
  if (byName) return byName;
  if (wb.SheetNames.length === 1) return wb.SheetNames[0];
  for (const name of wb.SheetNames) {
    const rows = XLSX.utils.sheet_to_json<any[]>(wb.Sheets[name], { header: 1, blankrows: false }).slice(0, 1);
    const hdr = (rows[0] || []).map(norm);
    if (hdr.some((h) => h.includes('meter factor')) && hdr.some((h) => h.includes('meter id'))) return name;
  }
  return null;
}

function resolveCols(header: any[]): Record<string, number> {
  const idx: Record<string, number> = {};
  const H = header.map(norm);
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    const i = H.findIndex((h) => aliases.some((a) => h === a || h.includes(a)));
    if (i >= 0) idx[field] = i;
  }
  return idx;
}

// Split each meter+commodity group into HIGH/LOW flow tracks (by the largest relative
// gap in its flow setpoints) and tag the commodity so the regression treats them
// separately. Single-setpoint meters are left untouched.
function splitByFlowTrack(records: any[]): any[] {
  const groups = new Map<string, any[]>();
  records.forEach((r) => { const k = `${r.meter_id}||${r.commodity}`; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(r); });
  const out: any[] = [];
  groups.forEach((rs) => {
    const flows = rs.map((r) => Number(r.flow_rate)).filter((f) => f > 0 && !isNaN(f));
    const uniq = Array.from(new Set(flows)).sort((a, b) => a - b);
    let threshold: number | null = null;
    if (uniq.length >= 2) {
      let gapRatio = 0, gi = -1;
      for (let i = 1; i < uniq.length; i++) { const ra = uniq[i] / uniq[i - 1]; if (ra > gapRatio) { gapRatio = ra; gi = i; } }
      if (gapRatio >= 1.2) threshold = uniq[gi];   // ≥20% jump = two distinct setpoints
    }
    rs.forEach((r) => {
      const fr = Number(r.flow_rate);
      const lbl = threshold !== null && fr > 0 ? (fr >= threshold ? 'HIGH' : 'LOW') : '';
      out.push(lbl ? { ...r, commodity: `${r.commodity} (${lbl})` } : { ...r });
    });
  });
  return out;
}

const RegressionUtility: React.FC = () => {
  const { getIdTokenClaims, user } = useAuth0();
  const [fileName, setFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [dragOver, setDragOver] = useState(false);
  const [records, setRecords] = useState<any[]>([]);
  const [mode, setMode] = useState<'full' | 'recent' | 'walkforward' | 'ensemble'>('ensemble');
  // Split each meter+commodity into HIGH/LOW flow tracks so a two-setpoint meter isn't
  // regressed as one mixed series. Off = combine (legacy behavior).
  const [splitFlow, setSplitFlow] = useState(true);
  // Future provings to project (0-5). null = use the persisted user setting the
  // server resolves; a number overrides it for this session.
  const [projection, setProjection] = useState<number | null>(null);
  // 'Predict Recent' count. null = use the persisted user setting.
  const [recentN, setRecentN] = useState<number | null>(null);
  const [zoomKey, setZoomKey] = useState<string | null>(null);
  // Recharts' Brush can't size its window until the container has a real width.
  // Inside a MUI Dialog that only happens after the open transition, so we hold the
  // chart back until then — otherwise the Brush mounts at width≈0 and shows all data.
  const [dialogEntered, setDialogEntered] = useState(false);
  // Brush window per combo, shared between the small card and the enlarged dialog
  // so enlarging preserves the zoom instead of snapping back to the default window.
  const [brushRanges, setBrushRanges] = useState<Record<string, [number, number]>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  // POST parsed rows to the engine for a given mode (full in-sample vs predict-recent).
  const runAnalysis = useCallback(async (
    recs: any[],
    m: 'full' | 'recent' | 'walkforward' | 'ensemble',
    proj?: number | null,
    recentN?: number | null,
    useSplit: boolean = splitFlow,
  ) => {
    setLoading(true); setError(null);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;
      const sendRecs = useSplit ? splitByFlowTrack(recs) : recs;
      const payload: any = { records: sendRecs, user_email: user?.email, mode: m };
      if (proj !== null && proj !== undefined) payload.projection_points = proj;
      if (recentN !== null && recentN !== undefined) payload.recent_n = recentN;
      const resp = await fetch(`${API_ENDPOINT}/regression-utility/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`Analysis failed (${resp.status})`);
      const data = await resp.json();
      const parsed = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
      setResult(parsed);
    } catch (e: any) {
      setError(e?.message || 'Failed to analyze.');
    } finally {
      setLoading(false);
    }
  }, [getIdTokenClaims, user, splitFlow]);

  const changeSplit = (v: boolean) => {
    setSplitFlow(v);
    if (records.length) runAnalysis(records, mode, projection, recentN, v);
  };

  const analyze = useCallback(async (file: File) => {
    setLoading(true); setError(null); setResult(null); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const sheetName = pickDataSheet(wb);
      if (!sheetName) throw new Error('Could not find a data sheet (expected a "Data extraction" tab or a sheet with Meter Factor / Meter ID columns).');

      const rows = XLSX.utils.sheet_to_json<any[]>(wb.Sheets[sheetName], { header: 1, blankrows: false });
      if (rows.length < 2) throw new Error('The data sheet has no rows.');
      const col = resolveCols(rows[0]);
      for (const req of ['date', 'meter_factor', 'meter_id', 'commodity']) {
        if (col[req] === undefined) throw new Error(`Missing required column "${req.replace('_', ' ')}" in the data sheet.`);
      }

      const records: any[] = [];
      for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row || row.length === 0) continue;
        const get = (f: string) => (col[f] !== undefined ? row[col[f]] : undefined);
        const dateVal = get('date');
        const mf = get('meter_factor');
        const mid = get('meter_id');
        const com = get('commodity');
        if (dateVal == null || mf == null || mf === '' || !mid || !com) continue;
        records.push({
          date: dateVal instanceof Date ? dateVal.toISOString() : dateVal,
          meter_factor: mf,
          flow_rate: get('flow_rate'),
          meter_temperature: get('meter_temperature'),
          density: get('density'),
          meter_pressure: get('meter_pressure'),
          meter_id: String(mid).trim(),
          commodity: String(com).trim(),
          direction: get('direction'),
          report_id: `${String(mid).trim()}_${String(com).trim()}_${r}`,
        });
      }
      if (records.length === 0) throw new Error('No valid data rows found (need Date, Meter Factor, Meter ID and Commodity).');

      setRecords(records);
      await runAnalysis(records, mode, projection, recentN);
    } catch (e: any) {
      setError(e?.message || 'Failed to analyze the file.');
      setLoading(false);
    }
  }, [runAnalysis, mode, projection, recentN]);

  const changeMode = (m: 'full' | 'recent' | 'walkforward' | 'ensemble' | null) => {
    if (!m || m === mode) return;
    setMode(m);
    if (records.length) runAnalysis(records, m, projection, recentN);
  };

  const changeProjection = (n: number) => {
    setProjection(n);
    if (records.length) runAnalysis(records, mode, n, recentN);
  };

  const changeRecentN = (n: number) => {
    setRecentN(n);
    if (records.length) runAnalysis(records, mode, projection, n);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) analyze(f);
  };

  const reset = () => { setResult(null); setError(null); setFileName(null); setRecords([]); if (inputRef.current) inputRef.current.value = ''; };

  const combos: any[] = result?.combos || [];
  const chartable = combos.filter((c) => ['success', 'low_quality'].includes(c.model?.status));
  const skipped = combos.filter((c) => !['success', 'low_quality'].includes(c.model?.status));
  // Resolve the enlarged combo by key from the CURRENT result so switching mode
  // inside the dialog re-runs the analysis and the chart refreshes in place.
  const zoomCombo = zoomKey ? combos.find((c) => `${c.meter_id}_${c.commodity}` === zoomKey) : null;
  const comboKey = (c: any) => `${c.meter_id}_${c.commodity}`;
  const setBrush = (key: string) => (range: [number, number]) =>
    setBrushRanges((prev) => {
      const cur = prev[key];
      if (cur && cur[0] === range[0] && cur[1] === range[1]) return prev;
      return { ...prev, [key]: range };
    });

  const modeTip = (m: string) =>
    m === 'recent' ? 'Trains on history and predicts the most recent 10 provings per meter'
    : m === 'walkforward' ? 'Walk-forward: predicts every proving from only the points before it (honest out-of-sample across the whole history)'
    : m === 'ensemble' ? 'Ensemble: blends several models, weighting each by its walk-forward (out-of-sample) accuracy'
    : 'Fits and predicts every proving in-sample';

  const ModeToggle = (
    <Tooltip title={modeTip(mode)}>
      <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_e, v) => changeMode(v)} disabled={loading}>
        <ToggleButton value="recent">Predict Recent</ToggleButton>
        <ToggleButton value="ensemble">Ensemble</ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );

  const SplitToggle = (
    <Tooltip title="Split each meter + commodity into HIGH/LOW flow tracks (by flow setpoint) so a two-setpoint meter isn't regressed as one mixed series. Off = combine.">
      <Chip size="small" clickable disabled={loading}
        variant={splitFlow ? 'filled' : 'outlined'} color={splitFlow ? 'primary' : 'default'}
        label={splitFlow ? 'HIGH/LOW split: on' : 'HIGH/LOW split: off'}
        onClick={() => changeSplit(!splitFlow)} />
    </Tooltip>
  );

  const recentValue = recentN ?? result?.settings?.recent_predict_points ?? 10;
  const RecentControl = mode === 'recent' ? (
    <Tooltip placement="top" title="How many of the most recent provings to predict (trains on everything before them). Excel splits by date; here it's a count.">
      <TextField
        select size="small" label="Recent" value={recentValue}
        onChange={(e) => changeRecentN(Number(e.target.value))}
        disabled={loading}
        sx={{ width: 90 }}
      >
        {[5, 10, 15, 20, 25, 30].map((n) => (
          <MenuItem key={n} value={n}>{n}</MenuItem>
        ))}
      </TextField>
    </Tooltip>
  ) : null;

  const projValue = projection ?? result?.settings?.projection_points ?? 0;
  const projApplies = mode === 'walkforward' || mode === 'ensemble';
  const ProjectionControl = (
    <Tooltip placement="top" title={projApplies
      ? 'Project this many future provings on each chart (damped-trend forecast of the out-of-sample path). Shown in Ensemble mode.'
      : 'Projections are drawn in Ensemble mode — switch to Ensemble to see them.'}>
      <TextField
        select size="small" label="Project" value={projValue}
        onChange={(e) => changeProjection(Number(e.target.value))}
        disabled={loading}
        sx={{ width: 96, opacity: projApplies ? 1 : 0.55 }}
      >
        {[0, 1, 2, 3, 4, 5].map((n) => (
          <MenuItem key={n} value={n}>{n === 0 ? 'Off' : `${n} ahead`}</MenuItem>
        ))}
      </TextField>
    </Tooltip>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Regression Utility</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload a proving-data Excel. We read the <strong>Data extraction</strong> sheet,
        group by meter &amp; product, and build a regression diagram for every combo using your
        regression settings. Nothing is saved — results clear when you leave; re-upload to regenerate.
      </Typography>

      {!result && (
        <Paper
          variant="outlined"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            p: 6, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed', borderWidth: 2,
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'action.hover' : 'transparent',
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xlsm,.xls"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) analyze(f); }}
          />
          {loading ? (
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Parsing {fileName} and computing regressions…
              </Typography>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={1}>
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="h6">Drop an Excel here, or click to browse</Typography>
              <Typography variant="body2" color="text.secondary">.xlsx / .xlsm / .xls</Typography>
            </Stack>
          )}
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {result && (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Chip icon={<InsertDriveFile />} label={fileName} />
              <Typography variant="body2"><strong>{result.total_records}</strong> records</Typography>
              <Typography variant="body2"><strong>{result.total_combos}</strong> meter/product combos</Typography>
              <Chip size="small" color="success" variant="outlined" label={`${chartable.length} modeled`} />
              {skipped.length > 0 && <Chip size="small" variant="outlined" label={`${skipped.length} insufficient`} />}
              <Divider orientation="vertical" flexItem />
              <Typography variant="caption" color="text.secondary">
                Settings: min {result.settings?.minimum_data_points} pts · min R² {result.settings?.minimum_r_squared} · {Math.round((result.settings?.confidence_level || 0.95) * 100)}% CI
              </Typography>
              <Box sx={{ flex: 1 }} />
              {loading && <CircularProgress size={18} />}
              {ModeToggle}
              {SplitToggle}
              {RecentControl}
              {ProjectionControl}
              <Button startIcon={<Refresh />} onClick={reset} variant="outlined" size="small">
                New file
              </Button>
            </Stack>
          </Paper>

          <Accordion variant="outlined" sx={{ mb: 2 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HelpOutline fontSize="small" color="action" />
                <Typography variant="subtitle2">How to read these charts</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <ChartGuide />
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))', gap: 2 }}>
            {chartable.map((c) => (
              <RegressionDiagram
                key={comboKey(c)}
                combo={c}
                onExpand={() => setZoomKey(comboKey(c))}
                brushRange={brushRanges[comboKey(c)] ?? null}
                onBrushChange={setBrush(comboKey(c))}
              />
            ))}
          </Box>

          {skipped.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Insufficient data ({skipped.length}) — fewer than {result.settings?.minimum_data_points} usable provings
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {skipped.map((c) => (
                  <Chip key={`${c.meter_id}_${c.commodity}`} size="small" variant="outlined"
                    label={`${c.meter_id} · ${c.commodity} (${c.n_records})`} />
                ))}
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {/* Enlarged view of a clicked diagram */}
      <Dialog
        open={Boolean(zoomKey)}
        onClose={() => setZoomKey(null)}
        maxWidth="lg"
        fullWidth
        TransitionProps={{
          onEntered: () => setDialogEntered(true),
          onExited: () => setDialogEntered(false),
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, pr: 5 }} flexWrap="wrap" useFlexGap>
            {zoomCombo && (
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {zoomCombo.meter_id} · {zoomCombo.commodity}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {loading && <CircularProgress size={18} />}
            {ModeToggle}
            {SplitToggle}
            {RecentControl}
            {ProjectionControl}
          </Stack>
          <IconButton
            onClick={() => setZoomKey(null)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
          >
            <Close />
          </IconButton>
          {zoomCombo && zoomKey && dialogEntered
            ? <RegressionDiagram
                key={zoomKey}
                combo={zoomCombo}
                height={520}
                brushRange={brushRanges[zoomKey] ?? null}
                onBrushChange={setBrush(zoomKey)}
              />
            : <Box sx={{ height: 520, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default RegressionUtility;
